import { type CommissionBillInfo, type QbJobPnl, type QbPnlGroup, commissionCents } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { ContactPicker } from "../components/forms";
import { useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Checkbox, Dialog, Field, Loading, Panel, PanelHeader, cx, formatCents, formatDate, formatDateTime, parseDollars } from "../components/ui";
import { useSession } from "../session";

/** The job's P&L tab: QuickBooks's numbers for the life of the job, with overhead and net profit. */
export function JobPnl({ jobId }: { jobId: string }) {
  const { me } = useSession();
  const q = useQuery({ queryKey: ["job-pnl", jobId], queryFn: () => get<QbJobPnl>(`/api/jobs/${jobId}/pnl`), staleTime: 60_000 });
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const share = useMutation({
    mutationFn: () => post<{ url: string }>(`/api/jobs/${jobId}/pnl/share`),
    onSuccess: async (r) => {
      setLink(r.url);
      setCopied(await navigator.clipboard.writeText(r.url).then(() => true, () => false));
    },
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert>{errorMessage(q.error)}</Alert>;
  const p = q.data!;
  const row = (label: string, cents: number, strong = false, tone?: "danger") => (
    <div className={cx("flex justify-between gap-4 py-1.5", strong && "border-t border-mist pt-2 font-semibold")}>
      <span>{label}</span>
      <span className={cx(tone === "danger" && "text-danger")}>{formatCents(cents)}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-5">
      <Panel>
        <PanelHeader
          title="Profit & loss"
          description={`Every QuickBooks transaction on this job, read live (accrual)${p.period.start && p.period.end ? `: ${formatDate(p.period.start)} to ${formatDate(p.period.end)}` : ""}. ${p.qbName ? `QuickBooks job: ${p.qbName}. ` : ""}Read ${formatDateTime(p.runAt, me?.company.timezone)}.`}
          action={
            <div className="flex flex-wrap gap-2">
              <Button className="px-3 py-1.5" busy={q.isFetching} onClick={() => q.refetch()}>
                Refresh
              </Button>
              <a href={`/api/jobs/${jobId}/pnl.pdf`} target="_blank" rel="noreferrer" className="rounded-[var(--radius-control)] border border-line px-3 py-1.5 text-sm font-semibold hover:bg-mist">
                PDF
              </a>
              <Button className="px-3 py-1.5" busy={share.isPending} onClick={() => share.mutate()}>
                Copy share link
              </Button>
            </div>
          }
        />
        {(share.error || link) && (
          <div className="px-5 pt-3 text-sm">
            {share.error ? (
              <Alert>{errorMessage(share.error)}</Alert>
            ) : (
              <div className="flex flex-col gap-1">
                <span className="text-ink-soft">{copied ? "Copied. " : ""}Anyone with this link can open today's P&L as a PDF for 30 days:</span>
                <input readOnly value={link!} onFocus={(e) => e.target.select()} className="w-full rounded-[var(--radius-control)] border border-line bg-mist px-3 py-2" />
              </div>
            )}
          </div>
        )}
        <div className="max-w-md px-5 py-4 text-[15px]">
          {row("Income", p.income.totalCents)}
          {row("Job materials", p.materials.totalCents)}
          {p.estimatedMaterials && (
            <div className="-mt-0.5 pb-1 text-sm text-muted">
              Material orders come to {formatCents(p.estimatedMaterials.cents)} at catalog prices
              {p.estimatedMaterials.unpriced > 0 && ` (${p.estimatedMaterials.unpriced} ${p.estimatedMaterials.unpriced === 1 ? "line has" : "lines have"} no price)`}. An estimate, not in these totals.
            </div>
          )}
          {row("Labor", p.labor.totalCents)}
          {row(`Overhead (${p.overheadPercent}% of income)`, p.overheadCents)}
          {row("Total expenses", p.expensesCents, true)}
          {row("Net profit", p.netCents, true, p.netCents < 0 ? "danger" : undefined)}
          {p.marginPercent !== null && <div className="pt-1 text-sm text-muted">Margin {p.marginPercent}%</div>}
          {(p.commission.lines.length > 0 || p.expectedCommission) && (
            <div className="mt-3 border-t border-line pt-2">
              <div className="pb-1 text-xs text-muted">Commission is figured on net profit, so it's shown here, not in it.</div>
              {p.expectedCommission && row(`Expected: ${p.expectedCommission.rep}, ${p.expectedCommission.percent}% of net profit`, p.expectedCommission.cents)}
              {row("Commission paid", p.commission.totalCents)}
              {row("After commission", p.afterCommissionCents, true, p.afterCommissionCents < 0 ? "danger" : undefined)}
              <CommissionBill jobId={jobId} pnl={p} />
            </div>
          )}
        </div>
      </Panel>
      <Lines title="Income" g={p.income} />
      <Lines title="Job materials" g={p.materials} />
      <Lines title="Labor" g={p.labor} />
      {p.commission.lines.length > 0 && <Lines title="Commission" g={p.commission} />}
    </div>
  );
}

function Lines({ title, g }: { title: string; g: QbPnlGroup }) {
  return (
    <Panel>
      <PanelHeader title={title} description={`${g.lines.length} ${g.lines.length === 1 ? "line" : "lines"} · ${formatCents(g.totalCents)}`} />
      {!g.lines.length ? (
        <p className="px-5 py-4 text-sm text-muted">None in QuickBooks.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted uppercase">
              <tr>
                <th className="px-5 py-2">Date</th>
                <th className="py-2">Type</th>
                <th className="py-2">Name</th>
                <th className="py-2">Memo</th>
                <th className="py-2">Account</th>
                <th className="px-5 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {g.lines.map((l, i) => (
                <tr key={`${l.txnId}-${i}`} className="border-t border-mist align-top">
                  <td className="px-5 py-2 whitespace-nowrap">{/^\d{4}-\d{2}-\d{2}$/.test(l.date) ? formatDate(l.date) : l.date}</td>
                  <td className="py-2 whitespace-nowrap">{[l.type, l.num && `#${l.num}`].filter(Boolean).join(" ")}</td>
                  <td className="py-2">{l.name}</td>
                  <td className="py-2 text-ink-soft">{l.memo}</td>
                  <td className="py-2 text-muted">{l.account}</td>
                  <td className="px-5 py-2 text-right whitespace-nowrap">{formatCents(l.amountCents)}</td>
                </tr>
              ))}
              <tr className="border-t border-line font-semibold">
                <td className="px-5 py-2" colSpan={5}>
                  Total {title.toLowerCase()}
                </td>
                <td className="px-5 py-2 text-right">{formatCents(g.totalCents)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/** The rep's commission bill: expected commission less what's already booked, made ready for approval in Receipts. */
function CommissionBill({ jobId, pnl }: { jobId: string; pnl: QbJobPnl }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["commission-bill", jobId], queryFn: () => get<CommissionBillInfo>(`/api/jobs/${jobId}/commission-bill`), enabled: can("receipts.approve") });
  const [open, setOpen] = useState(false);
  if (!can("receipts.approve") || !q.data) return null;
  const info = q.data;
  const e = pnl.expectedCommission;
  const due = e ? Math.max(0, e.cents - pnl.commission.totalCents) : 0;
  const waiting = info.bills.find((b) => b.status !== "approved");
  const approved = info.bills.filter((b) => b.status === "approved").reduce((n, b) => n + b.totalCents, 0);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
      {waiting ? (
        <span>
          Commission bill: {formatCents(waiting.totalCents)}, waiting for approval in{" "}
          <Link to="/receipts" className="font-semibold text-brand hover:underline">
            Receipts
          </Link>
          .
        </span>
      ) : !info.rep ? (
        <span className="text-muted">No commissioned rep on this job.</span>
      ) : !e ? (
        <span className="text-muted">{pnl.netCents <= 0 ? "No commission on a loss." : `No commission rate for ${info.rep.name} (set it on their user).`}</span>
      ) : due <= 0 ? (
        <span className="text-muted">Commission is booked{approved ? ` (${formatCents(approved)} billed here)` : ""}.</span>
      ) : (
        <>
          {approved > 0 && <span className="text-muted">{formatCents(approved)} billed here already.</span>}
          <Button className="px-3 py-1.5" onClick={() => setOpen(true)}>
            Create commission bill ({formatCents(due)})
          </Button>
        </>
      )}
      {open && e && info.rep && (
        <CommissionDialog
          jobId={jobId}
          info={info}
          netCents={pnl.netCents}
          bookedCents={pnl.commission.totalCents}
          percent={e.percent}
          onClose={() => setOpen(false)}
          onDone={async () => (setOpen(false), await qc.invalidateQueries({ queryKey: ["commission-bill", jobId] }))}
        />
      )}
    </div>
  );
}

function CommissionDialog({ jobId, info, netCents, bookedCents, percent: startPercent, onClose, onDone }: { jobId: string; info: CommissionBillInfo; netCents: number; bookedCents: number; percent: number; onClose: () => void; onDone: () => void }) {
  const rep = info.rep!;
  const memoFor = (p: string) => `Commission on job: ${p}% of ${formatCents(netCents)} net profit (${rep.name})`;
  const dueFor = (p: string) => {
    const bps = Math.round(Number(p) * 100);
    return Number.isFinite(bps) && bps >= 0 ? Math.max(0, commissionCents(netCents, bps) - bookedCents) : 0;
  };
  const [percent, setPercent] = useState(String(startPercent));
  const [amount, setAmount] = useState((dueFor(String(startPercent)) / 100).toFixed(2));
  const [memo, setMemo] = useState(memoFor(String(startPercent)));
  const [memoTouched, setMemoTouched] = useState(false);
  const [text, setText] = useState(false);
  const [email, setEmail] = useState(false);
  const [vendor, setVendor] = useState<{ id: string; name: string } | null>(rep.vendorContact ?? null);
  const cents = parseDollars(amount);
  const bps = Math.round(Number(percent) * 100);
  const percentOk = percent.trim() !== "" && Number.isFinite(bps) && bps >= 0 && bps <= 10_000;
  const onPercent = (p: string) => {
    setPercent(p);
    setAmount((dueFor(p) / 100).toFixed(2));
    if (!memoTouched) setMemo(memoFor(p));
  };
  const make = useMutation({
    mutationFn: () =>
      post<{ told: { text?: string | null; email?: string | null } }>(`/api/jobs/${jobId}/commission-bill`, {
        amountCents: cents,
        memo,
        netCents: Math.max(0, netCents),
        percentBps: bps,
        billedBeforeCents: bookedCents,
        tell: { text, email },
        ...(rep.vendorContact ? {} : { vendorContactId: vendor?.id }),
      }),
    onSuccess: (r) => {
      const failed = [r.told.text ? `Text: ${r.told.text}` : "", r.told.email ? `Email: ${r.told.email}` : ""].filter(Boolean);
      if (failed.length) window.alert(`The bill was made, but the rep wasn't told. ${failed.join(" ")}`);
      onDone();
    },
  });
  return (
    <Dialog
      title={`Commission bill: ${rep.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={make.isPending} disabled={!cents || cents <= 0 || !vendor || !info.category || !percentOk} onClick={() => make.mutate()}>
            Make the bill
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {make.error && <Alert>{errorMessage(make.error)}</Alert>}
        {!info.category && <Alert>Pick the expense category for commissions first: in Settings → QuickBooks, map a category to your commission account.</Alert>}
        <p className="text-ink-soft">A bill to the rep on this job, ready for approval in Receipts; approving it sends it to QuickBooks. The amount is net profit × the percentage, less what's already booked{bookedCents > 0 ? ` (${formatCents(bookedCents)})` : ""}.</p>
        {rep.vendorContact ? (
          <div>
            <span className="font-semibold">Paid as</span> {rep.vendorContact.name}
          </div>
        ) : (
          <ContactPicker label={`The vendor ${rep.name} is paid as`} value={vendor} onChange={setVendor} category="vendor" hint="Remembered for their next commission." />
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Commission %" inputMode="decimal" value={percent} onChange={(e) => onPercent(e.target.value)} hint={`Of ${formatCents(netCents)} net profit. This bill only; ${rep.name}'s usual rate stays ${startPercent}%.`} />
          <Field label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <Field label="Memo" value={memo} onChange={(e) => (setMemoTouched(true), setMemo(e.target.value))} />
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-ink-soft">Tell {rep.name}</span>
          <Checkbox label={rep.canText ? "Text them" : "Text them (no cell phone on their user)"} checked={text} disabled={!rep.canText} onChange={setText} />
          <Checkbox label={rep.canEmail ? "Email them" : "Email them (no email on their user)"} checked={email} disabled={!rep.canEmail} onChange={setEmail} />
          {(text || email) && <p className="text-xs text-muted">Just the net profit, the percentage and the commission{bookedCents > 0 ? ", and what was billed before" : ""}.</p>}
        </div>
        {info.category && <p className="text-xs text-muted">Category: {info.category}</p>}
      </div>
    </Dialog>
  );
}
